import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { addDays, format, isValid } from 'date-fns';
import { Text, Pane, Flex } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { Attributes } from '@leverege/ui-attributes';
import { GroupActions, Relationship } from '@leverege/molten/imagine';
import { getCompanyId } from './Util';
import S from './Billing.css';
import { getCurrentMonthlyBilling } from '../../../util/Interface';
import { PaymentStatusRenderer, TotalDueRenderer, TotalPaidRenderer } from '../../../attributes/renderers/billing';
import { formatCurrency } from '../../../util/Formatting';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const paymentAttributes = {
  'bill.paymentStatus': PaymentStatusRenderer,
  'bill.totalPaid': TotalPaidRenderer,
  'bill.totalDue': TotalDueRenderer
};
function BillingTotalCard(props) {
  const {
    parentItem,
    sharedProps,
    chargesAggs,
    creditsAggs
  } = props;
  const [companyContract, setCompanyContract] = useState({});
  const [billingReport, setBillingReport] = useState({});
  const systemId = parentItem?.data?.systemId;
  const locationId = sharedProps?.match?.params?.locationId || (parentItem?.type === 'location' ? parentItem.id : '');
  const relationship = Relationship.getByPath('company.companyContracts');
  const actions = sharedProps?.actions;
  const paginator = sharedProps?.paginator;
  const getAddendumInfo = () => {
    const {
      addendumChargesActions,
      addendumCreditsActions
    } = props;
    Promise.all([GlobalState.dispatch(addendumChargesActions.search({
      limit: 0,
      aggs: {
        charges: {
          type: 'stats',
          field: 'data/amount'
        }
      },
      filter: {
        field: 'data/type',
        type: 'equals',
        value: 'charge'
      }
    }, {
      ttl: 30000
    })), GlobalState.dispatch(addendumCreditsActions.search({
      limit: 0,
      aggs: {
        credits: {
          type: 'stats',
          field: 'data/amount'
        }
      },
      filter: {
        field: 'data/type',
        type: 'equals',
        value: 'credit'
      }
    }, {
      ttl: 30000
    }))]).then(res => {
      // 
    });
  };
  let groupActions, companyId;
  useEffect(() => {
    const execute = async () => {
      // card is on drilldown dashboard
      if (sharedProps?.item) {
        companyId = sharedProps?.parentItem?.data?.data?.company?.id || sharedProps?.match?.params?.companyId;
        if (!companyId) {
          companyId = await getCompanyId(locationId);
        }
        const companyContractId = sharedProps.parentItem?.data?.data?.companyContractId;
        const match = {
          ...sharedProps.actions.match,
          params: {
            companyId
          }
        };
        groupActions = new GroupActions({
          relationship: relationship.createResolved(match),
          match,
          ttl: 30000
        });
        const contractActions = groupActions.item(companyContractId).get();
        GlobalState.dispatch(contractActions).then(res => {
          setCompanyContract(res);
        });
        setBillingReport(sharedProps?.parentItem?.data);
        getAddendumInfo();

        // card is in billing viewer
      } else {
        companyId = parentItem?.type === 'company' ? parentItem.id : parentItem?.data?.data?.company?.id;
        getCurrentMonthlyBilling(systemId, companyId, locationId).then(res => {
          setBillingReport(res);
          setCompanyContract(res.data.activeCompanyContract);
        });
      }
    };
    execute();
  }, [systemId, companyId]);
  const totalBill = billingReport.data?.totalBill || 0;
  const date = new Date();
  const endDate = new Date(billingReport?.data?.endDate);
  let dueDate, dueDateFormatted;
  if (isValid(endDate)) {
    dueDate = addDays(endDate, 30);
    dueDateFormatted = format(dueDate, 'MM/dd/yy');
  }
  const currentMonthDays = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const gracePeriodActive = new Date(companyContract?.data?.gracePeriodEndDate) > date;
  let billingCycleDescription = '';
  if (endDate > date) {
    const daysLeft = currentMonthDays - date.getDate() + 1;
    billingCycleDescription = `${daysLeft} days left in billing cycle`;
  } else {
    billingCycleDescription = 'Billing Cycle Complete';
  }
  const addendumChargeAmount = chargesAggs?.charges?.sum || 0;
  const addendumCreditAmount = creditsAggs?.credits?.sum || 0;
  const getDetailRenderers = useCallback(() => {
    const original = sharedProps?.item ? paginator?.pageItems[0] : null;
    if (!original) {
      return null;
    }
    return /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsx("hr", {
        style: {
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: 'var(--color-base100)',
          borderBottom: 'none'
        }
      }), /*#__PURE__*/_jsx(Pane, {
        className: S.grid,
        variant: "detailItemContent",
        children: Object.keys(paymentAttributes).map(attrName => {
          const attr = Attributes.getAttribute(attrName, 'bill');
          const Renderer = paymentAttributes[attrName];
          return /*#__PURE__*/_jsxs(React.Fragment, {
            children: [/*#__PURE__*/_jsx(Flex, {
              justify: "flex-start",
              children: /*#__PURE__*/_jsx(Text, {
                variant: "billDetailLabel",
                children: attr.displayName
              })
            }), /*#__PURE__*/_jsx(Flex, {
              justify: "flex-end",
              children: /*#__PURE__*/_jsx(Renderer, {
                original: original,
                variant: "detailValue"
              })
            })]
          }, attrName);
        })
      })]
    });
  }, [sharedProps?.item, actions]);
  return /*#__PURE__*/_jsx(Pane, {
    variant: "billingCard",
    className: S.billingCard,
    children: /*#__PURE__*/_jsxs(Pane, {
      variant: "billingCardContent",
      children: [/*#__PURE__*/_jsxs(Pane, {
        variant: "billingCardBody",
        layout: "flex:colM",
        children: [/*#__PURE__*/_jsxs(Flex, {
          variant: "rowM",
          children: [/*#__PURE__*/_jsx(Text, {
            variant: "body",
            children: "Total Charges This Month"
          }), gracePeriodActive && /*#__PURE__*/_jsx(Text, {
            variant: "gracePeriodPill",
            children: "Grace Period Active"
          })]
        }), /*#__PURE__*/_jsx(Text, {
          variant: "billingCardFigure",
          children: formatCurrency(totalBill + addendumChargeAmount - addendumCreditAmount)
        }), /*#__PURE__*/_jsx(Text, {
          variant: "bodySmallBold",
          children: billingCycleDescription
        }), !!dueDateFormatted && /*#__PURE__*/_jsxs(Flex, {
          variant: "rowS",
          children: [/*#__PURE__*/_jsx(Text, {
            variant: "bodySmall",
            children: "Due Date:"
          }), /*#__PURE__*/_jsx(Text, {
            variant: dueDate < date ? 'bodySmallBoldError' : 'bodySmallBold',
            children: dueDateFormatted
          })]
        }), getDetailRenderers()]
      }), /*#__PURE__*/_jsx(Text, {
        variant: "billingCardLink",
        children: /*#__PURE__*/_jsx("a", {
          href: "https://help.recovr.biz/en/articles/4890707-how-do-i-pay-my-bill",
          rel: "noreferrer",
          target: "_blank",
          children: "How do I pay my bill?"
        })
      })]
    })
  });
}
export default connect((state, ownProps) => {
  const {
    sharedProps
  } = ownProps;
  const actions = sharedProps?.actions;
  if (!sharedProps?.item) {
    return {
      ...sharedProps
    };
  }
  const childActions = actions.child('addendum');
  const addendumChargesActions = new GroupActions({
    match: childActions.match,
    relationship: childActions.relationship.createResolved(childActions.match),
    queryName: 'totalAddendumChargesAgg'
  });
  const addendumCreditsActions = new GroupActions({
    match: childActions.match,
    relationship: childActions.relationship.createResolved(childActions.match),
    queryName: 'totalAddendumCreditsAgg'
  });
  const chargesAggs = addendumChargesActions.getList(state, {
    getAll: true
  })?.aggs;
  const creditsAggs = addendumCreditsActions.getList(state, {
    getAll: true
  })?.aggs;
  return {
    props: ownProps,
    models: state.models,
    addendumChargesActions,
    addendumCreditsActions,
    chargesAggs,
    creditsAggs
  };
})(BillingTotalCard);